import { useList, useUpdate } from '@refinedev/core'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import type { BaseRecord } from '@refinedev/core'
import { flaskApi } from '@/lib/flaskApi'
import { useQuery } from '@tanstack/react-query'
import { History, Eye, Play, ChevronDown, ChevronRight, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { UNIT_NAMING_TYPE_OPTIONS } from '@/constants/agencyRegistration'

const BULK_UNIT_NAMING_UNCHANGED = '__unchanged__'

interface Company extends BaseRecord {
  id: string
  company_name: string
  use_livekit_video_service: boolean
  enable_google_calendar_integration: boolean
  enable_outlook_calendar_integration: boolean
  enable_linkedin_integration: boolean
  unit_naming_type?: string
}

interface Subscription extends BaseRecord {
  id: string
  company_id: string
  active: boolean
  positions: number
  text_interviews: number
  video_interviews: number
  in_person_interviews: number
  cover_letter_interviews: number
  candidates: number
  users: number
  organizations: number
  linkedin_searches: number
}

interface AuditEntry {
  at: string
  company_id: string
  company_name: string
  changes: Record<string, { from: unknown; to: unknown }>
}

interface DiffEntry {
  company_id: string
  company_name: string
  changes: Record<string, { from: unknown; to: unknown }>
}

interface DryRunResult {
  affected: number
  preview: DiffEntry[]
}

const TOGGLES: Array<{ field: keyof Company; label: string }> = [
  { field: 'use_livekit_video_service',           label: 'LiveKit' },
  { field: 'enable_google_calendar_integration',  label: 'Google Cal' },
  { field: 'enable_outlook_calendar_integration', label: 'Outlook Cal' },
  { field: 'enable_linkedin_integration',          label: 'LinkedIn' },
]

const SUB_FIELDS = [
  'positions', 'text_interviews', 'video_interviews', 'in_person_interviews',
  'cover_letter_interviews', 'candidates', 'users', 'organizations',
  'linkedin_searches',
] as const

const PRESETS = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'LiveKit off, no calendar integrations, LinkedIn off',
    preset: { use_livekit_video_service: false, enable_google_calendar_integration: false, enable_outlook_calendar_integration: false, enable_linkedin_integration: false },
  },
  {
    id: 'premium',
    label: 'Premium',
    description: 'LiveKit on, all calendar integrations, LinkedIn on',
    preset: { use_livekit_video_service: true, enable_google_calendar_integration: true, enable_outlook_calendar_integration: true, enable_linkedin_integration: true },
  },
  {
    id: 'google_cal',
    label: 'Google Calendar',
    description: 'Enable only Google Calendar integration',
    preset: { use_livekit_video_service: false, enable_google_calendar_integration: true, enable_outlook_calendar_integration: false, enable_linkedin_integration: false },
  },
]

function DryRunPreview({ result, onClose, onApply }: { result: DryRunResult; onClose: () => void; onApply: () => void }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dry Run Preview — {result.affected} companies affected</DialogTitle>
        </DialogHeader>
        {result.preview.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No changes detected for any company.</p>
        ) : (
          <div className="space-y-3 py-2">
            {result.preview.map((entry) => (
              <div key={entry.company_id} className="rounded-xl border border-border/60 p-4">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">{entry.company_name}</p>
                <div className="space-y-1">
                  {Object.entries(entry.changes).map(([field, diff]) => (
                    <div key={field} className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-[10px]">{field.replace(/_/g, ' ')}</Badge>
                      <span className="text-muted-foreground">{String(diff.from)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-[#076698] font-bold">{String(diff.to)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {result.affected > 0 && (
            <Button className="bg-[#076698] hover:bg-[#055580]" onClick={onApply}>
              Apply to {result.affected} Companies
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function HistoryPanel() {
  const [open, setOpen] = useState(false)
  const { data: history, refetch, isFetching } = useQuery<AuditEntry[]>({
    queryKey: ['service-config', 'history'],
    queryFn: () => flaskApi.get<AuditEntry[]>('/service-config/history').then((r) => r.data),
    enabled: false,
  })

  const handleOpen = () => {
    setOpen(true)
    refetch()
  }

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={handleOpen}>
        <History size={13} />
        Change History
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service Config Change History</DialogTitle>
          </DialogHeader>
          {isFetching ? (
            <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
          ) : !history || history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No changes recorded in this session.</p>
          ) : (
            <div className="space-y-2 py-2">
              {history.map((entry, i) => (
                <div key={i} className="rounded-xl border border-border/60 p-3.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{entry.company_name}</p>
                    <span className="text-[10px] text-slate-400">{new Date(entry.at).toLocaleString()}</span>
                  </div>
                  <div className="space-y-0.5">
                    {Object.entries(entry.changes).map(([field, diff]) => (
                      <div key={field} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-slate-600 dark:text-slate-400">{field.replace(/_/g, ' ')}:</span>
                        <span>{String(diff.from)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-[#076698] font-semibold">{String(diff.to)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export function ServiceConfigurationPage() {
  const [search, setSearch] = useState('')
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkValues, setBulkValues] = useState({
    use_livekit: false,
    google_cal: false,
    outlook_cal: false,
    unit_naming_type: '',
  })
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
  const [dryRunning, setDryRunning] = useState(false)
  const [applyStatus, setApplyStatus] = useState<'idle' | 'applying' | 'done' | 'error'>('idle')
  const [presetExpanded, setPresetExpanded] = useState(false)
  const [applyMessage, setApplyMessage] = useState('')

  const { data: companiesData, isLoading } = useList<Company>({
    resource: 'companies',
    filters: search ? [{ field: 'company_name', operator: 'contains', value: search }] : [],
    pagination: { pageSize: 50 },
  })

  const { data: subscriptionsData } = useList<Subscription>({
    resource: 'subscriptions',
    filters: [{ field: 'active', operator: 'eq', value: true }],
    pagination: { pageSize: 200 },
  })

  const { mutate: updateCompany } = useUpdate()
  const { mutate: updateSubscription } = useUpdate()

  const companies = companiesData?.data ?? []
  const subscriptions = subscriptionsData?.data ?? []
  const getSub = (cid: string) => subscriptions.find((s) => s.company_id === cid)

  const toggleCompany = (id: string, field: string, current: boolean) =>
    updateCompany({ resource: 'companies', id, values: { [field]: !current } })

  const updateSubField = (subId: string, field: string, value: number) =>
    updateSubscription({ resource: 'subscriptions', id: subId, values: { [field]: value } })

  const bulkPreset: Record<string, string | boolean> = {
    use_livekit_video_service: bulkValues.use_livekit,
    enable_google_calendar_integration: bulkValues.google_cal,
    enable_outlook_calendar_integration: bulkValues.outlook_cal,
  }
  if (bulkValues.unit_naming_type) {
    bulkPreset.unit_naming_type = bulkValues.unit_naming_type
  }

  const handleDryRun = async () => {
    setDryRunning(true)
    try {
      const res = await flaskApi.post<DryRunResult>('/service-config/dry-run', { preset: bulkPreset })
      setDryRunResult(res.data)
    } finally {
      setDryRunning(false)
    }
  }

  const handleBulkApply = async () => {
    setApplyStatus('applying')
    setApplyMessage('')
    try {
      const res = await flaskApi.post<{ updated: number }>('/service-config/bulk-apply', { preset: bulkPreset })
      setApplyStatus('done')
      setApplyMessage(`Updated ${res.data.updated} companies successfully.`)
    } catch {
      setApplyStatus('error')
      setApplyMessage('Bulk apply failed.')
    }
    setBulkOpen(false)
    setDryRunResult(null)
  }

  const applyPresetDirect = async (presetObj: Record<string, boolean>) => {
    try {
      await flaskApi.post('/service-config/bulk-apply', { preset: presetObj })
    } catch { /* silent */ }
  }

  return (
    <TooltipProvider>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Service Configuration</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Manage company feature toggles, unit naming (branches / departments), and subscription limits.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <HistoryPanel />
            <Button className="bg-[#076698] hover:bg-[#055580]" size="sm" onClick={() => setBulkOpen(true)}>
              Bulk Update
            </Button>
          </div>
        </div>

        <div className="bento-card overflow-hidden">
          <button
            className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors"
            onClick={() => setPresetExpanded((e) => !e)}
          >
            {presetExpanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-200">Preset Profiles</span>
            <span className="text-xs text-slate-400 dark:text-slate-500">Apply a named configuration to all companies at once</span>
          </button>
          {presetExpanded && (
            <div className="px-5 pb-5 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-border/30">
              {PRESETS.map((p) => (
                <div key={p.id} className="rounded-xl border border-border/60 p-4 space-y-2">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{p.label}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{p.description}</p>
                  <div className="flex gap-2 mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs gap-1"
                      onClick={async () => {
                        setDryRunning(true)
                        try {
                          const res = await flaskApi.post<DryRunResult>('/service-config/dry-run', { preset: p.preset })
                          setDryRunResult(res.data)
                          setBulkOpen(false)
                          setBulkValues({
                            use_livekit: p.preset.use_livekit_video_service,
                            google_cal: p.preset.enable_google_calendar_integration,
                            outlook_cal: p.preset.enable_outlook_calendar_integration,
                            unit_naming_type: '',
                          })
                        } finally {
                          setDryRunning(false)
                        }
                      }}
                      disabled={dryRunning}
                    >
                      <Eye size={11} /> Preview
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs gap-1 bg-[#076698] hover:bg-[#055580]"
                      onClick={() => applyPresetDirect(p.preset)}
                    >
                      <Play size={11} /> Apply
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {applyStatus !== 'idle' && (
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-xl border text-xs',
            applyStatus === 'done' || applyStatus === 'applying'
              ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
              : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
          )}>
            {applyStatus === 'applying' ? <Loader2 size={13} className="animate-spin" /> : applyStatus === 'done' ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {applyMessage || 'Applying changes…'}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Input
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        <div className="bento-card overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur z-10">
              <TableRow>
                <TableHead className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Company</TableHead>
                {TOGGLES.map((t) => (
                  <TableHead key={t.field as string} className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">{t.label}</TableHead>
                ))}
                <TableHead className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 min-w-[200px]">
                  Unit naming
                </TableHead>
                {SUB_FIELDS.map((f) => (
                  <TableHead key={f} className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">
                    {f.replace(/_/g, ' ')}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4 + TOGGLES.length + 1 + SUB_FIELDS.length} className="text-center text-sm text-muted-foreground py-10">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => {
                  const sub = getSub(company.id as string)
                  return (
                    <TableRow key={company.id as string} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                      <TableCell className="text-xs font-bold text-slate-700 dark:text-slate-200">
                        {company.company_name}
                      </TableCell>
                      {TOGGLES.map(({ field }) => (
                        <TableCell key={field as string}>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={!!company[field]}
                              onCheckedChange={() =>
                                toggleCompany(
                                  company.id as string,
                                  field as string,
                                  company[field] as boolean
                                )
                              }
                            />
                            <span className="text-xs text-muted-foreground">
                              {company[field] ? 'On' : 'Off'}
                            </span>
                          </div>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Select
                          value={company.unit_naming_type ?? 'branch_department'}
                          onValueChange={(v) =>
                            updateCompany({
                              resource: 'companies',
                              id: company.id as string,
                              values: { unit_naming_type: v ?? 'branch_department' },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 max-w-[220px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {UNIT_NAMING_TYPE_OPTIONS.map(({ value, label }) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {sub ? (
                        SUB_FIELDS.map((field) => (
                          <TableCell key={field}>
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <span className="inline-block">
                                    <Input
                                      type="number"
                                      defaultValue={((sub as Record<string, unknown>)[field] as number) ?? 0}
                                      className="h-7 w-16 text-xs"
                                      onBlur={(e) =>
                                        updateSubField(
                                          sub.id as string,
                                          field,
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                    />
                                  </span>
                                }
                              />
                              <TooltipContent>
                                <p className="text-xs capitalize">{field.replace(/_/g, ' ')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                        ))
                      ) : (
                        <TableCell colSpan={SUB_FIELDS.length} className="text-xs text-muted-foreground italic">
                          No active subscription
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>

        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Update All Companies</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {[
                { key: 'use_livekit' as const, label: 'LiveKit Video Service' },
                { key: 'google_cal' as const,  label: 'Google Calendar Integration' },
                { key: 'outlook_cal' as const, label: 'Outlook Calendar Integration' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm">{label}</span>
                  <Switch
                    checked={bulkValues[key]}
                    onCheckedChange={(checked) =>
                      setBulkValues({ ...bulkValues, [key]: checked })
                    }
                  />
                </div>
              ))}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm">Unit naming (all companies)</span>
                <Select
                  value={bulkValues.unit_naming_type || BULK_UNIT_NAMING_UNCHANGED}
                  onValueChange={(v) => {
                    const next = v ?? BULK_UNIT_NAMING_UNCHANGED
                    setBulkValues({
                      ...bulkValues,
                      unit_naming_type: next === BULK_UNIT_NAMING_UNCHANGED ? '' : next,
                    })
                  }}
                >
                  <SelectTrigger className="h-9 w-full max-w-sm text-sm">
                    <SelectValue placeholder="Do not change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BULK_UNIT_NAMING_UNCHANGED}>Do not change</SelectItem>
                    {UNIT_NAMING_TYPE_OPTIONS.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={handleDryRun}
                disabled={dryRunning}
              >
                {dryRunning ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                Preview Changes
              </Button>
              <Button className="bg-[#076698] hover:bg-[#055580]" onClick={handleBulkApply} disabled={applyStatus === 'applying'}>
                Apply to All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {dryRunResult && (
          <DryRunPreview
            result={dryRunResult}
            onClose={() => setDryRunResult(null)}
            onApply={() => {
              handleBulkApply()
              setDryRunResult(null)
            }}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
